import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { Bell, Calendar, AlertCircle, ShieldAlert, X, Shield, PhoneForwarded } from 'lucide-react'

// Define the notification types and structure
type NotificationItem = {
  id: string
  type: 'interview' | 'followup' | 'insurance'
  title: string
  message: string
  date: string
  read: boolean
  linkId?: string
}

export function NotificationCenter() {
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchNotifications()
    // Minimal polling per aggregare le info
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  async function fetchNotifications() {
    setLoading(true)
    const newNotifs: NotificationItem[] = []
    const todayStr = new Date().toISOString()
    const in3Days = new Date()
    in3Days.setDate(in3Days.getDate() + 3)

    // 1. Upcoming Interviews
    const { data: interviews } = await supabase
      .from('interviews')
      .select('id, nome, cognome, data_colloquio')
      .gte('data_colloquio', new Date().toISOString().slice(0, 10))
      .lte('data_colloquio', in3Days.toISOString().slice(0, 10))

    if (interviews) {
      interviews.forEach(iv => {
         newNotifs.push({
           id: `int_${iv.id}`,
           type: 'interview',
           title: 'Colloquio Imminente',
           message: `${iv.cognome} ${iv.nome} — ${new Date(iv.data_colloquio).toLocaleDateString('it-IT')}`,
           date: iv.data_colloquio,
           read: false,
           linkId: iv.id
         })
      })
    }

    // 2. Follow-ups required
    const { data: followups } = await supabase
      .from('interviews')
      .select('id, nome, cognome, data_follow_up')
      .lte('data_follow_up', new Date().toISOString().slice(0, 10))
      .not('data_follow_up', 'is', null)

    if (followups) {
      followups.forEach(iv => {
         newNotifs.push({
           id: `fup_${iv.id}`,
           type: 'followup',
           title: 'Follow-up Richiesto',
           message: `${iv.cognome} ${iv.nome} richiede contatto`,
           date: iv.data_follow_up,
           read: false,
           linkId: iv.id
         })
      })
    }

    // 3. Expired / Expiring Insurances (Archivio)
    const { data: nurses } = await supabase
      .from('nurses')
      .select('id, nome, cognome, polizza_scadenza')
      .lte('polizza_scadenza', in3Days.toISOString().slice(0, 10))

    if (nurses) {
      nurses.forEach(nurse => {
         const isExpired = new Date(nurse.polizza_scadenza) < new Date()
         newNotifs.push({
           id: `ins_${nurse.id}`,
           type: 'insurance',
           title: isExpired ? 'Polizza Scaduta' : 'Polizza in Scadenza',
           message: `${nurse.cognome} ${nurse.nome} — Scad: ${new Date(nurse.polizza_scadenza).toLocaleDateString('it-IT')}`,
           date: nurse.polizza_scadenza,
           read: false,
           linkId: nurse.id
         })
      })
    }

    setNotifications(newNotifs.sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime()))
    setLoading(false)
  }

  const unreadCount = notifications.length

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse border-2 border-white" />
        )}
      </button>

      {isOpen && (
        <div className="absolute top-12 right-0 w-80 bg-white/95 backdrop-blur-xl border border-slate-200/60 shadow-2xl rounded-2xl overflow-hidden z-[100] animate-modal-in">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <h3 className="font-black text-slate-800 flex items-center gap-2">
              <Bell className="w-4 h-4 text-indigo-500" />
              Notifiche
              {unreadCount > 0 && <span className="bg-indigo-100 text-indigo-700 text-[10px] px-2 py-0.5 rounded-full">{unreadCount}</span>}
            </h3>
            <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
               <div className="p-8 text-center text-slate-400 text-sm flex justify-center"><Bell className="w-5 h-5 animate-bounce text-slate-300" /></div>
            ) : notifications.length === 0 ? (
               <div className="p-8 text-center text-slate-400 text-sm">
                 Tutto in regola! Nessuna notifica.
               </div>
            ) : (
               <div className="divide-y divide-slate-100">
                 {notifications.map(n => (
                   <div key={n.id} className="p-4 hover:bg-slate-50/80 transition-colors cursor-pointer flex gap-3 group">
                     <div className={`mt-0.5 shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${n.type === 'interview' ? 'bg-indigo-100 text-indigo-600' : n.type === 'insurance' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                       {n.type === 'interview' && <Calendar className="w-4 h-4" />}
                       {n.type === 'insurance' && <ShieldAlert className="w-4 h-4" />}
                       {n.type === 'followup' && <PhoneForwarded className="w-4 h-4" />}
                     </div>
                     <div>
                       <div className="text-xs font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">{n.title}</div>
                       <div className="text-xs text-slate-500 mt-1 leading-snug">{n.message}</div>
                     </div>
                   </div>
                 ))}
               </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
